import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {  useOnboarding } from '../../../context/OnboardingContext';
import { PrivateWizardLayout, WizardContainer, WizardCard, WizardActions } from '../WizardLayout';
import Stepper from '../Stepper';
import {
  saveAutomationPreferences,
  generateAutomation,
  getGenerationStatus,
  startTestRun,
  getRunStatus,
  completeOnboardingSession
} from '../../../services/wizardApi';
import { toast } from 'react-toast';
import {
  LuCheck, LuLoader, LuRocket, LuPlay, LuSkipForward,
  LuActivity, LuClock, LuTrophy, LuUserPlus, LuCalendar, LuArrowRight, LuArrowLeft,
  LuMonitor, LuServer
} from 'react-icons/lu';
import { useWizardTestCaseSummary } from '../../../hooks/API/wizard.api';
import axiosInstance from '../../../api/axiosInstance';
import { STEP_STATUS, TEST_CREATION_MODES } from '../../../Config/Onboarding/constant';
import useDiscoveryProgressSocket, { EXECUTION_PROGRESS_EVENT } from '../../../hooks/useDiscoveryProgressSocket';
import { formatSecondsToMinSec } from '../../../utils/formatSecondToMinSec';

// STEP 6: Automation Preferences
export const Step6Preferences = () => {
  const navigate = useNavigate();
  const { automationPrefs, setAutomationPrefs, nextStep, previousStep } = useOnboarding();
  const [prefs, setPrefs] = useState(automationPrefs);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (field, value) => {
    setPrefs(prev => ({ ...prev, [field]: value }));
  };

  const handleContinue = async () => {
    setIsLoading(true);
    try {
      await saveAutomationPreferences(prefs);
      setAutomationPrefs(prefs);
      nextStep();
      toast.success('Preferences saved');
      navigate('/app/onboarding?step=7');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper currentStep={6} completedSteps={[1, 2, 3, 4, 5]} />
        
        <WizardCard title="Automation Preferences" subtitle="Configure how tests should run">
          <div className="wizard-form-group">
            <label className="wizard-form-label">Target Browser(s)</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
              {['chrome', 'firefox', 'edge'].map(browser => (
                <div key={browser} className="wizard-form-checkbox">
                  <input
                    type="checkbox"
                    checked={prefs.browsers.includes(browser)}
                    onChange={(e) => {
                      const newBrowsers = e.target.checked
                        ? [...prefs.browsers, browser]
                        : prefs.browsers.filter(b => b !== browser);
                      handleChange('browsers', newBrowsers);
                    }}
                  />
                  <label>{browser.charAt(0).toUpperCase() + browser.slice(1)}</label>
                </div>
              ))}
            </div>
          </div>

          <div className="wizard-form-group">
            <div className="wizard-form-checkbox">
              <input
                type="checkbox"
                checked={prefs.headless}
                onChange={(e) => handleChange('headless', e.target.checked)}
              />
              <label>Run in Headless Mode</label>
            </div>
          </div>

          <div className="wizard-form-group">
            <div className="wizard-form-checkbox">
              <input
                type="checkbox"
                checked={prefs.parallel}
                onChange={(e) => handleChange('parallel', e.target.checked)}
              />
              <label>Enable Parallel Execution</label>
            </div>
          </div>

          {prefs.parallel && (
            <div className="wizard-form-group">
              <label className="wizard-form-label">Worker Threads</label>
              <input
                type="number"
                min="1"
                max="10"
                className="wizard-form-input"
                value={prefs.workers}
                onChange={(e) => handleChange('workers', parseInt(e.target.value))}
              />
            </div>
          )}

          <div className="wizard-form-group">
            <label className="wizard-form-label">Test Pack</label>
            <select
              className="wizard-form-select"
              value={prefs.testPack}
              onChange={(e) => handleChange('testPack', e.target.value)}
            >
              <option value="smoke">Smoke Tests</option>
              <option value="regression">Regression Suite</option>
              <option value="full">Full Suite</option>
            </select>
          </div>

          <WizardActions
            showBack={true}
            onBack={() => {
              previousStep();
              navigate('/app/onboarding?step=5');
            }}
            showNext={true}
            onNext={handleContinue}
            nextLabel="Continue"
            nextLoading={isLoading}
          />
        </WizardCard>
      </WizardContainer>
    </PrivateWizardLayout>
  );
};

// STEP 7: Generate Automation
export const Step7Generate = () => {
  const navigate = useNavigate();
  const { setGenerationJob, nextStep, previousStep } = useOnboarding();
  const [stage, setStage] = useState('parsing');
  const [progress, setProgress] = useState(0);
  const [logs, setLogs] = useState(['Starting automation generation...']);
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState('processing');

  useEffect(() => {
    startGeneration();
  }, []);

  const startGeneration = async () => {
    try {
      const response = await generateAutomation({});
      if (response.success) {
        setJobId(response.data.generate_job_id);
        pollStatus(response.data.generate_job_id);
      }
    } catch (error) {
      toast.error('Failed to start generation');
    }
  };

  const pollStatus = async (id) => {
    const interval = setInterval(async () => {
      try {
        const response = await getGenerationStatus(id);
        if (response.success) {
          setProgress(response.data.progress);
          setStage(response.data.stage);
          setLogs(prev => [...prev, response.data.message]);

          if (response.data.status === 'completed') {
            clearInterval(interval);
            setStatus('completed');
            setGenerationJob({ jobId: id, status: 'completed', progress: 100 });
            setTimeout(() => {
              nextStep();
              navigate('/app/onboarding?step=8');
            }, 2000);
          }
        }
      } catch (error) {
        clearInterval(interval);
        setStatus('error');
      }
    }, 1500);
  };

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper currentStep={7} completedSteps={[1, 2, 3, 4, 5, 6]} />
        
        <WizardCard title="Generating Automation" subtitle="Please wait while we create your test automation">
          <div className="wizard-progress">
            <div className="wizard-progress-bar">
              <div className="wizard-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="wizard-progress-text">{progress}% Complete</span>
          </div>

          <div className="wizard-progress-stages">
            {['parsing', 'mapping', 'generating', 'validating', 'packaging'].map(s => (
              <div key={s} className={`wizard-progress-stage ${s === stage ? 'active' : ''} ${logs.some(l => l.toLowerCase().includes(s)) ? 'completed' : ''}`}>
                <span className="wizard-progress-stage-icon">
                  {s === stage ? (
                    <LuLoader size={20} className="spinner" />
                  ) : logs.some(l => l.toLowerCase().includes(s)) ? (
                    <LuCheck size={20} />
                  ) : (
                    <div style={{ width: '20px', height: '20px', borderRadius: '50%', border: '2px solid var(--border-color)' }} />
                  )}
                </span>
                <span className="wizard-progress-stage-text">{s.charAt(0).toUpperCase() + s.slice(1)}</span>
              </div>
            ))}
          </div>

          <div className="wizard-logs">
            {logs.map((log, i) => (
              <div key={i} className="wizard-log-line info">{log}</div>
            ))}
          </div>

          {status === 'error' && (
            <WizardActions
              showBack={true}
              onBack={() => {
                previousStep();
                navigate('/app/onboarding?step=6');
              }}
              showNext={false}
            >
              <button className="wizard-btn wizard-btn-primary" onClick={startGeneration}>
                Retry
              </button>
            </WizardActions>
          )}
        </WizardCard>
      </WizardContainer>
    </PrivateWizardLayout>
  );
};

// UI Discovery only: pick Local Machine (Robitest agent) or Server execution
const UiDiscoveryRunModeModal = ({ isOpen, onClose, onRun }) => {
  const [selectedMode, setSelectedMode] = useState('local');

  useEffect(() => {
    if (isOpen) setSelectedMode('local');
  }, [isOpen]);

  if (!isOpen) return null;

  const modes = [
    {
      value: 'local',
      title: 'Local Machine',
      description: 'Run on your machine through the Robitest agent with the browser visible',
      Icon: LuMonitor
    },
    {
      value: 'server',
      title: 'Server (Headless)',
      description: 'Run on server infrastructure in headless mode',
      Icon: LuServer
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 13000 }}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden flex flex-col" style={{ zIndex: 14000 }}>
        <div className="rt-modal__header">
          <div>
            <h2>Run Tests</h2>
            <p>Choose an execution mode to run all compiled test scripts</p>
          </div>
        </div>

        <div className="p-6 space-y-3">
          <label className="block text-sm font-semibold text-gray-700">
            Execution Mode <span className="text-red-500">*</span>
          </label>
          {modes.map(({ value, title, description, Icon }) => (
            <label
              key={value}
              className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                selectedMode === value
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 bg-gray-50 hover:border-blue-300'
              }`}
            >
              <input
                type="radio"
                name="uiDiscoveryRunMode"
                value={value}
                checked={selectedMode === value}
                onChange={(e) => setSelectedMode(e.target.value)}
                className="w-4 h-4 text-blue-600 cursor-pointer"
              />
              <div className="ml-3 flex-1">
                <p className="font-medium text-gray-700">{title}</p>
                <p className="text-xs text-gray-600 mt-1">{description}</p>
              </div>
              <Icon size={22} className="text-blue-600 flex-shrink-0" />
            </label>
          ))}
        </div>

        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onRun(selectedMode)}
            className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <LuPlay size={16} strokeWidth={2} /> Run Now
          </button>
        </div>
      </div>
    </div>
  );
};

// STEP 8: Run First Test
export const Step8RunTest = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setRunJob, previousStep, data, markStepCompleted, setStepStatus, goToStep, setUiDiscovery } = useOnboarding();
  const [isRunning, setIsRunning] = useState(false);
  const [runId, setRunId] = useState(null);
  const [runStatus, setRunStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  // UI Discovery only: ignore late progress frames once the API has responded
  const executionFinishedRef = useRef(false);
  // UI Discovery only: execution mode picker + waiting on the local agent
  const [isRunModeModalOpen, setIsRunModeModalOpen] = useState(false);
  const awaitingLocalProjectRef = useRef(false);
  const mode = searchParams.get('mode') || data?.testCreationMode;
  const isRecorderMode = mode === TEST_CREATION_MODES.RECORDER || mode === 'recorder';
  // UI Discovery flow (steps 6-12): run test is step 11, summary is step 12.
  // Kept separate so the recorder / AI flows below are untouched.
  const isUiDiscoveryMode = mode === TEST_CREATION_MODES.UI_DISCOVERY || mode === 'ui';
  const runStep = isRecorderMode ? 9 : isUiDiscoveryMode ? 11 : 8;
  const stepperCurrentStep = runStep;
  const stepperCompletedSteps = isRecorderMode
    ? [1, 2, 3, 4, 5, 6, 7, 8]
    : isUiDiscoveryMode
      ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
      : [1, 2, 3, 4, 5, 6, 7];
  const summaryStep = isRecorderMode ? 10 : isUiDiscoveryMode ? 12 : 9;
  const summaryPath = isRecorderMode
    ? '/app/onboarding?step=10&mode=recorder'
    : isUiDiscoveryMode
      ? '/app/onboarding?step=12&mode=ui'
      : '/app/onboarding?step=9';

  const goToSummary = () => {
    goToStep(summaryStep);
    navigate(summaryPath);
  };

  const testCaseIds = data?.testCases;
  const projectId = data?.project?.project_id
    || data?.project?.projectId
    || data?.project?.id
    || data?.uiDiscovery?.projectId;

  // UI Discovery only: the notifications socket is connected as soon as this
  // screen mounts, i.e. before /crawler/execute/project_ui_test is called, so
  // no progress frame pushed during the (long running) request is missed.
  // Other flows pass enabled: false and never open a socket.
  const { progress: executionProgress } = useDiscoveryProgressSocket({
    enabled: isUiDiscoveryMode,
    messageType: EXECUTION_PROGRESS_EVENT
  });

  // Live pass / fail / total / progress from execution_progress frames (message.data)
  useEffect(() => {
    if (!executionProgress || executionFinishedRef.current) return;

    const executing = executionProgress.currently_executing;

    setRunStatus((prev) => ({
      ...prev,
      passed: Number(executionProgress.passed_count) || 0,
      failed: Number(executionProgress.failed_count) || 0,
      completed: Number(executionProgress.completed_count) || 0,
      total: Number(executionProgress.total_scripts) || prev?.total || 0,
      progress: Math.min(100, Math.round(Number(executionProgress.progress_percentage) || 0)),
      elapsedSeconds: Number(executionProgress.elapsed_seconds) || 0,
      currentTestCase: executing?.title || prev?.currentTestCase || '',
      status: "running"
    }));

    if (executing?.title) {
      const label = executing.test_case_id ? `Test case ${executing.test_case_id}` : 'Test case';
      const line = `${label}: ${executing.title} — running`;
      setLogs((prev) => (prev[prev.length - 1] === line ? prev : [...prev, line]));
    }
  }, [executionProgress]);


  useEffect(() => {
    const handleMessage = (event) => {
      const msgData = event.data;

      if (msgData?.source !== "ROBITEST_AGENT") return;
      // UI Discovery local project runs are handled by their own listener below
      if (awaitingLocalProjectRef.current) return;

      if (msgData.type === "TEST_PROGRESS") {
   

        setRunStatus((prev) => ({
          ...prev,
          passed: msgData.passed ?? prev?.passed ?? 0,
          failed: msgData.failed ?? prev?.failed ?? 0,
          total: msgData.total ?? prev?.total ?? 0,
          progress: msgData.percent ?? prev?.progress ?? 0,
          status: msgData.percent >= 100 ? "completed" : "running",
        }));

        setLogs((prev) => [
          ...prev,
          `Test case ${msgData.test_case_id}: ${msgData.completed ?? 0}/${msgData.total ?? 0} completed`,
        ]);

        if (msgData.percent >= 100) {
          setIsRunning(false);
          toast.success("Test run completed!");
          setRunJob({ runId: null, status: "completed", results: msgData });
          markStepCompleted(runStep);
          setStepStatus(summaryStep, STEP_STATUS.IN_PROGRESS);
          setTimeout(() => {
            goToSummary();
          }, 1500);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);


  const handleRunNow = async () => {
    setIsRunning(true);
    setRunStatus({ passed: 0, failed: 0, total: 0, progress: 0, status: "running" });
    setLogs([]);

    try {
      window.postMessage(
        {
          source: "ROBITEST_CLOUD",
          action: "RUN_CLOUD_TEST",
          test_case_ids: testCaseIds,
          project_id: projectId,
          
        },
        "*"
      );

      toast.success(`Test case(s) ready to run!`);
    } catch (error) {
      toast.error("Failed to start test run");
      setIsRunning(false);
    }
  };

  const startUiDiscoveryRun = (initialLog) => {
    setIsRunning(true);
    setRunStatus({ passed: 0, failed: 0, total: 0, progress: 0, status: "running" });
    setLogs([initialLog]);
    executionFinishedRef.current = false;
  };

  // UI Discovery: shared by server (API response) and local (agent event)
  // execution — builds the summary and hands it to Step9Summary through the
  // wizard context.
  const applyUiDiscoveryExecutionResult = ({ summary, results, message }) => {
    // Navigation / summary come from the final result; stop applying
    // websocket frames from here on.
    executionFinishedRef.current = true;

    // No compiled scripts / nothing executed — stay on this screen and let
    // the user compile scripts or retry. Do NOT advance to the summary page.
    if (!Array.isArray(results) || results.length === 0) {
      setIsRunning(false);
      setRunStatus(null);
      setLogs([]);
      toast.info(message);
      return;
    }

    // The local agent reports `duration`, the server API `duration_seconds`
    const normalizedResults = results.map((r) => ({
      ...r,
      duration_seconds: r.duration_seconds ?? r.duration
    }));

    const total = summary?.total || 0;
    const passed = summary?.passed || 0;
    const failed = summary?.failed || 0;
    const avgDurationSeconds = normalizedResults.reduce(
      (sum, r) => sum + (Number(r.duration_seconds) || 0), 0
    ) / normalizedResults.length;

    const executionSummary = {
      passed,
      failed,
      total,
      results: normalizedResults,
      message,
      // Shaped like the wizard summary API so Step9Summary can render it as-is
      avg_pass_rate: total > 0 ? Math.round((passed / total) * 100) : 0,
      total_test_cases: total,
      avg_execution_time: Math.round(avgDurationSeconds * 100) / 100
    };

    setRunStatus({
      passed,
      failed,
      total,
      progress: 100,
      status: "completed"
    });
    setLogs(normalizedResults.map((r) => (
      `Test case ${r.test_case_id}: ${r.status}` +
      `${r.duration_seconds ? ` (${r.duration_seconds}s)` : ''}` +
      `${r.error_message ? ` — ${r.error_message}` : ''}`
    )));

    setRunJob({ runId: null, status: "completed", results: executionSummary });
    setUiDiscovery({ executionSummary });
    setIsRunning(false);

    if (failed > 0) {
      toast.error(message);
    } else {
      toast.success(message);
    }

    markStepCompleted(runStep);
    setStepStatus(summaryStep, STEP_STATUS.IN_PROGRESS);
    setTimeout(() => {
      goToSummary();
    }, 1500);
  };

  // UI Discovery (server): execute every compiled script for the project in one call
  const handleUiDiscoveryRunNow = async () => {
    startUiDiscoveryRun("Executing compiled test scripts for this project...");

    try {
      // skipGlobalToast: the message is surfaced below with pass/fail styling
      const response = await axiosInstance.post(
        '/crawler/execute/project_ui_test',
        { project_id: projectId, headless: true },
        { skipGlobalToast: true }
      );

      applyUiDiscoveryExecutionResult({
        summary: response?.data?.summary || { total: 0, passed: 0, failed: 0 },
        results: response?.data?.results || [],
        message: response?.message || 'Project execution completed.'
      });
    } catch (error) {
      console.error('Error executing project UI tests:', error);
      executionFinishedRef.current = true;
      toast.error(error.response?.data?.message || 'Failed to execute tests');
      setIsRunning(false);
      setRunStatus(null);
    }
  };

  // UI Discovery (local): ask the Robitest agent to run the project; the
  // result arrives as a PROJECT_EXECUTION_SUCCESS / _ERROR message (see effect below)
  const handleUiDiscoveryLocalRunNow = () => {
    startUiDiscoveryRun("Executing compiled test scripts on your local machine...");
    awaitingLocalProjectRef.current = true;

    window.postMessage(
      {
        source: "ROBITEST_CLOUD",
        action: "TRIGGER_LOCAL_PROJECT_EXECUTION",
        projectId: Number(projectId),
        headless: false,
        autoHeal: true
      },
      "*"
    );
  };

  // Latest handler in a ref so the listener below always sees fresh state
  const applyUiDiscoveryResultRef = useRef(applyUiDiscoveryExecutionResult);
  applyUiDiscoveryResultRef.current = applyUiDiscoveryExecutionResult;

  useEffect(() => {
    if (!isUiDiscoveryMode) return undefined;

    const handleLocalProjectMessage = (event) => {
      const msg = event.data;
      if (msg?.source !== "ROBITEST_AGENT" || !awaitingLocalProjectRef.current) return;

      if (msg.type === "PROJECT_EXECUTION_SUCCESS") {
        if (msg.projectId != null && projectId != null && String(msg.projectId) !== String(projectId)) return;

        awaitingLocalProjectRef.current = false;
        const data = msg.payload?.data || {};
        applyUiDiscoveryResultRef.current({
          summary: data.summary || { total: 0, passed: 0, failed: 0 },
          results: data.results || [],
          message: msg.payload?.message || 'Project execution completed.'
        });
      } else if (msg.type === "PROJECT_EXECUTION_ERROR") {
        awaitingLocalProjectRef.current = false;
        executionFinishedRef.current = true;
        toast.error(msg.error || 'Failed to execute tests on local machine');
        setIsRunning(false);
        setRunStatus(null);
        setLogs([]);
      }
    };

    window.addEventListener("message", handleLocalProjectMessage);
    return () => {
      window.removeEventListener("message", handleLocalProjectMessage);
      awaitingLocalProjectRef.current = false;
    };
  }, [isUiDiscoveryMode, projectId]);

  const handleUiDiscoveryRunModeSelected = (selectedMode) => {
    setIsRunModeModalOpen(false);
    if (selectedMode === 'local') {
      handleUiDiscoveryLocalRunNow();
    } else {
      handleUiDiscoveryRunNow();
    }
  };

  const handleUiDiscoveryRunLater = () => {
    setRunJob({ runId: null, status: "skipped" });
    navigate('/dashboard');
  };

  const handleUiDiscoveryBack = () => {
    // Compile & Generate (review) is step 10 in the UI Discovery flow
    goToStep(10);
    navigate('/app/onboarding?step=10&mode=ui');
  };

  const pollRunStatus = async (id) => {
    const interval = setInterval(async () => {
      try {
        const response = await getRunStatus(id);
        if (response.success) {
          setRunStatus(response.data);
          setLogs((prev) => [
            ...prev,
            `${response.data.current_test || "Running"}: ${response.data.message}`,
          ]);

          if (response.data.status === "completed") {
            clearInterval(interval);
            setRunJob({ runId: id, status: "completed", results: response.data });
            setIsRunning(false);
            toast.success("Test run completed!");
            setTimeout(() => {
              goToSummary();
            }, 1500);
          }
        }
      } catch (error) {
        clearInterval(interval);
        setIsRunning(false);
      }
    }, 2000);
  };

  const handleSkip = () => {
    setRunJob({ runId: null, status: "skipped" });
    goToSummary();
  };

  const handleBack = () => {
    if (isRecorderMode) {
      goToStep(8);
      navigate('/app/onboarding?step=8&mode=recorder');
      return;
    }
    if (isUiDiscoveryMode) {
      handleUiDiscoveryBack();
      return;
    }
    previousStep();
    navigate("/app/onboarding?step=9");
  };

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper currentStep={stepperCurrentStep} completedSteps={stepperCompletedSteps} />

        <WizardCard title="Run Your First Test" subtitle="Execute tests and see results in real-time">
          {!isRunning && !runStatus ? (
            <div style={{ textAlign: "center", padding: "3rem" }}>
              <div style={{ marginBottom: "1.5rem", display: "flex", justifyContent: "center" }}>
                <LuRocket size={72} strokeWidth={1.5} style={{ color: "var(--primary-color)" }} />
              </div>
              <h3 style={{ fontSize: "var(--font-size-xl)", marginBottom: "1rem" }}>
                Ready to run your first test!
              </h3>
              <p style={{ color: "var(--text-secondary)", marginBottom: "2rem" }}>
                Click the button below to execute your test suite and see automation in action.
              </p>
              <div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "1rem",
                flexWrap: "wrap",
              }}>
                {isUiDiscoveryMode && (
                  <button
                    className="wizard-btn wizard-btn-secondary"
                    onClick={handleUiDiscoveryBack}
                  >
                    ← Back
                  </button>
                )}
                <button
                  className="wizard-btn wizard-btn-primary"
                  onClick={isUiDiscoveryMode ? () => setIsRunModeModalOpen(true) : handleRunNow}
                >
                  <LuPlay size={18} strokeWidth={2} /> Run Now
                </button>
                <button
                  className="wizard-btn wizard-btn-secondary"
                  onClick={isUiDiscoveryMode ? handleUiDiscoveryRunLater : handleSkip}
                >
                  <LuSkipForward size={18} strokeWidth={2} /> Run Later
                </button>
              </div>
            </div>
          ) : (
            <>
              {runStatus && (
                <div style={{ marginBottom: "2rem" }}>
                  <div
                    style={{
                      display: "flex",
                      gap: "2rem",
                      justifyContent: "center",
                      marginBottom: "1rem",
                    }}
                  >
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "2rem",
                          fontWeight: "bold",
                          color: "var(--success-color)",
                        }}
                      >
                        {runStatus.passed || 0}
                      </div>
                      <div style={{ color: "var(--text-secondary)" }}>Passed</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "2rem",
                          fontWeight: "bold",
                          color: "var(--danger-color)",
                        }}
                      >
                        {runStatus.failed || 0}
                      </div>
                      <div style={{ color: "var(--text-secondary)" }}>Failed</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "2rem",
                          fontWeight: "bold",
                          color: "var(--text-primary)",
                        }}
                      >
                        {runStatus.total || 0}
                      </div>
                      <div style={{ color: "var(--text-secondary)" }}>Total</div>
                    </div>
                  </div>
                  <div className="wizard-progress-bar">
                    <div
                      className="wizard-progress-fill"
                      style={{ width: `${runStatus.progress || 0}%` }}
                    />
                  </div>

                  {/* Live websocket details (UI Discovery run) */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "1rem",
                      flexWrap: "wrap",
                      marginTop: "0.75rem",
                      color: "var(--text-secondary)",
                      fontSize: "var(--font-size-sm)",
                    }}
                  >
                    <span>{runStatus.progress || 0}% complete</span>
                    {runStatus.total > 0 && (
                      <span>
                        {runStatus.completed ?? ((runStatus.passed || 0) + (runStatus.failed || 0))}/
                        {runStatus.total} executed
                      </span>
                    )}
                    {runStatus.elapsedSeconds > 0 && (
                      <span>Elapsed {formatSecondsToMinSec(Math.round(runStatus.elapsedSeconds))}</span>
                    )}
                  </div>

                  {runStatus.status === "running" && runStatus.currentTestCase && (
                    <div
                      style={{
                        textAlign: "center",
                        marginTop: "0.5rem",
                        color: "var(--text-secondary)",
                      }}
                    >
                      <LuLoader
                        size={14}
                        strokeWidth={2}
                        className="spinner"
                        style={{ verticalAlign: "middle", marginRight: "0.4rem" }}
                      />
                      Currently executing: <strong>{runStatus.currentTestCase}</strong>
                    </div>
                  )}
                </div>
              )}

              <div className="wizard-logs" style={{ maxHeight: "300px" }}>
                {logs.map((log, i) => (
                  <div key={i} className="wizard-log-line info">
                    {log}
                  </div>
                ))}
              </div>

              {runStatus?.status === "completed" && (
                <WizardActions
                  showBack={true}
                  showNext={true}
                  onBack={handleBack}
                  onNext={() => {
                    goToSummary();
                  }}
                  nextLabel="View Results"
                />
              )}
            </>
          )}
        </WizardCard>
      </WizardContainer>

      {isUiDiscoveryMode && (
        <UiDiscoveryRunModeModal
          isOpen={isRunModeModalOpen}
          onClose={() => setIsRunModeModalOpen(false)}
          onRun={handleUiDiscoveryRunModeSelected}
        />
      )}
    </PrivateWizardLayout>
  );
};

// STEP 9: Results Summary
export const Step9Summary = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { runJob, completeOnboarding, sessionId, testCases, uiDiscovery, data: onboardingData, goToStep } = useOnboarding();
  const mode = searchParams.get('mode') || onboardingData?.testCreationMode;
  const isRecorderMode = mode === TEST_CREATION_MODES.RECORDER || mode === 'recorder';
  // UI Discovery gets its summary from the crawler/execute/project_ui_test response
  // stored in context, so the wizard summary API is not called for that flow.
  const isUiDiscoveryMode = mode === TEST_CREATION_MODES.UI_DISCOVERY || mode === 'ui';
  const stepperCurrentStep = isRecorderMode ? 10 : isUiDiscoveryMode ? 12 : 9;
  // Mirrors the run-step numbering in Step8RunTest so Back always lands on
  // the "Run Your First Test" screen for whichever flow led here.
  const runStep = isRecorderMode ? 9 : isUiDiscoveryMode ? 11 : 8;
  const runPath = isRecorderMode
    ? '/app/onboarding?step=9&mode=recorder'
    : isUiDiscoveryMode
      ? '/app/onboarding?step=11&mode=ui'
      : '/app/onboarding?step=8';

  const handleBack = () => {
    goToStep(runStep);
    navigate(runPath);
  };
    const payload = {
    test_case_ids: testCases
  };

  const {
    data: apiSummary,
    isLoading: isSummaryLoading,
    error
  } = useWizardTestCaseSummary(payload, { enabled: !isUiDiscoveryMode });

  const discoverySummary = uiDiscovery?.executionSummary;
  const data = isUiDiscoveryMode
    ? {
        avg_pass_rate: discoverySummary?.avg_pass_rate ?? 0,
        total_test_cases: discoverySummary?.total_test_cases ?? 0,
        avg_execution_time: discoverySummary?.avg_execution_time ?? 0
      }
    : apiSummary;
  const isLoading = isUiDiscoveryMode ? false : isSummaryLoading;

  const results = runJob.results || { passed: 4, failed: 1, total: 5, duration: 45 };


  const handleGoToDashboard = async () => {
    try {
      if (sessionId) {
        await completeOnboardingSession(sessionId);
      }
      completeOnboarding?.();
      // Set flag to show welcome banner
      localStorage.setItem('onboarding_just_completed', 'true');
      toast.success('Welcome to Robitest! 🎉');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error completing onboarding:', error);
      navigate('/dashboard');
    }
  };

  const passRate = Math.round((results.passed / results.total) * 100);
  const timeSaved = Math.round(results.total * 2.5); // Mock calculation

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper
          currentStep={stepperCurrentStep}
          completedSteps={isUiDiscoveryMode ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] : [1, 2, 3, 4, 5, 6, 7, 8]}
        />
        
        <WizardCard title="Your automation is ready" subtitle="Congratulations — your workspace, project and first tests are set up.">
          <div className="ob-summary-hero">
            <span className="ob-summary-hero__icon" aria-hidden="true">
              <LuTrophy size={28} strokeWidth={1.75} />
            </span>
            <div>
              <p className="ob-summary-hero__title">Setup complete</p>
              <p className="ob-summary-hero__sub">Here&apos;s a quick look at your first run.</p>
            </div>
          </div>

          {isLoading ? (
            <div className="ob-metrics" aria-busy="true" aria-label="Loading summary">
              {[0, 1, 2].map((i) => (
                <div key={i} className="ob-metric is-loading">
                  <span className="ob-skeleton" style={{ width: 28, height: 28, borderRadius: 8 }} />
                  <span className="ob-skeleton" style={{ width: 72, height: 28 }} />
                  <span className="ob-skeleton" style={{ width: 96, height: 12 }} />
                </div>
              ))}
            </div>
          ) : (
            <div className="ob-metrics">
              <div className="ob-metric ob-metric--success">
                <span className="ob-metric__icon" aria-hidden="true"><LuCheck size={18} /></span>
                <span className="ob-metric__value">{data.avg_pass_rate}%</span>
                <span className="ob-metric__label">Pass Rate</span>
              </div>
              <div className="ob-metric ob-metric--primary">
                <span className="ob-metric__icon" aria-hidden="true"><LuActivity size={18} /></span>
                <span className="ob-metric__value">{data.total_test_cases}</span>
                <span className="ob-metric__label">Test Cases</span>
              </div>
              <div className="ob-metric ob-metric--warning">
                <span className="ob-metric__icon" aria-hidden="true"><LuClock size={18} /></span>
                <span className="ob-metric__value">{data.avg_execution_time}{isUiDiscoveryMode ? 's' : 'm'}</span>
                <span className="ob-metric__label">Time Saved</span>
              </div>
            </div>
          )}

          <div className="ob-section" style={{ marginTop: 28, paddingTop: 28, borderTop: '1px solid var(--border)' }}>
            <div className="ob-section__head">
              <div>
                <h3 className="ob-section__title">What&apos;s next?</h3>
                <p className="ob-section__desc">Keep the momentum going with your team.</p>
              </div>
            </div>
            <div className="ob-choice-grid ob-choice-grid--2">
              <button
                type="button"
                className="ob-choice ob-choice--compact"
                onClick={() => {
                  navigate("/dashboard/settings", { state: { activeSection: "invite" } });
                }}
              >
                <span className="ob-choice__icon" aria-hidden="true"><LuUserPlus size={18} /></span>
                <span className="ob-choice__body">
                  <span className="ob-choice__title">Invite Team Members</span>
                  <span className="ob-choice__desc">Collaborate on tests and results.</span>
                </span>
              </button>
              <button
                type="button"
                className="ob-choice ob-choice--compact"
                onClick={() => {
                  navigate("/dashboard/test-plans");
                }}
              >
                <span className="ob-choice__icon" aria-hidden="true"><LuCalendar size={18} /></span>
                <span className="ob-choice__body">
                  <span className="ob-choice__title">Schedule Test Runs</span>
                  <span className="ob-choice__desc">Run your suites automatically.</span>
                </span>
              </button>
            </div>
          </div>

          <div className="ob-actions">
            <button type="button" className="ob-btn ob-btn--secondary ob-actions__start" onClick={handleBack}>
              <LuArrowLeft size={16} aria-hidden="true" />
              Back
            </button>
            <button type="button" className="ob-btn ob-btn--primary" onClick={handleGoToDashboard}>
              Go to Dashboard
              <LuArrowRight size={16} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
        </WizardCard>
      </WizardContainer>
    </PrivateWizardLayout>
  );
};